import type { CSSProperties } from 'react'
import type { BreakdownDim, CashCollectionsData, MonthCash } from '../types'

// Business unit › Department › Category for the breakdown chart (schema v2).
// Each level is one map per month; all three sum to net sales (department and
// category within integer rounding). In the Category view the amounts the
// pipeline could only place at department level (`departmentUnresolved`) join
// as `unresolved:<departmentId>` series so that level still sums to net sales.

export const LEVELS: readonly BreakdownDim[] = ['Business unit', 'Department', 'Category']

/** Lower-case noun for captions ("split by department"). */
export const LEVEL_NOUN: Record<BreakdownDim, string> = {
  'Business unit': 'business unit',
  Department: 'department',
  Category: 'category',
}

/** Glossary term (see @/lib/glossary) that explains each level. */
export const LEVEL_TERM: Record<BreakdownDim, string> = {
  'Business unit': 'businessUnit',
  Department: 'department',
  Category: 'category',
}

export const UNRESOLVED = 'unresolved:'

type LevelMaps = Pick<MonthCash, 'unit' | 'department' | 'category' | 'departmentUnresolved'>

/** The values a month (or an aggregate) carries at one level, keyed by id. */
export function levelValues(m: LevelMaps | undefined, dim: BreakdownDim): Record<string, number> {
  if (!m) return {}
  if (dim === 'Business unit') return m.unit ?? {}
  if (dim === 'Department') return m.department ?? {}
  const out: Record<string, number> = { ...(m.category ?? {}) }
  for (const [dep, v] of Object.entries(m.departmentUnresolved ?? {})) out[UNRESOLVED + dep] = v
  return out
}

export const levelTotal = (vals: Record<string, number>) => Object.values(vals).reduce((a, b) => a + b, 0)

/** True when the payload carries the schema v2 hierarchy the breakdown reads. */
export const hasHierarchy = (data: CashCollectionsData) =>
  !!(data.units?.length && data.departments?.length && data.categories?.length && data.months.some((m) => m.unit))

/** One drawable series at a level: a business unit, department, category, or a
 *  department's unresolved (department-level) amount in the Category view. */
export interface Series {
  key: string
  /** Short name — the bar / legend label. */
  name: string
  /** Parent department name (Category view only) — the legend group. */
  group?: string
  /** Full name for titles: "Lab › Milling" in the Category view. */
  label: string
  color: string
  unresolved?: boolean
}

// Distinct, refined palette (warm-leaning, clearly distinguishable). One colour
// per department; a business unit takes its first department's colour and a
// category a tint of its department's, so the three views read as one system.
const PALETTE = ['#AE9056', '#5E6E8C', '#4E9B8A', '#C9794E', '#8E6FA6', '#94A84F', '#C8696B']
const NEUTRAL = '#9A938A'
const TINTS = [0, 28, 46, 60, 70]
const tint = (hex: string, i: number) => {
  const pct = TINTS[i % TINTS.length]
  return pct ? `color-mix(in srgb, ${hex}, white ${pct}%)` : hex
}

/** Swatch / segment fill: solid, or hatched for an unresolved amount. */
export function seriesFill(s: Series): CSSProperties {
  return s.unresolved
    ? { backgroundImage: `repeating-linear-gradient(135deg, ${s.color} 0 3px, transparent 3px 6px)` }
    : { backgroundColor: s.color }
}

/** Every series at a level, in the classification's display order. */
export function levelSeries(data: CashCollectionsData, dim: BreakdownDim): Series[] {
  const units = data.units ?? []
  const departments = data.departments ?? []
  const categories = data.categories ?? []

  const deptColor: Record<string, string> = {}
  let pi = 0
  for (const d of departments) deptColor[d.id] = d.id === 'unclassified' ? NEUTRAL : PALETTE[pi++ % PALETTE.length]

  if (dim === 'Business unit') {
    return units.map((u, i) => {
      const first = departments.find((d) => d.unit === u.id)
      return { key: u.id, name: u.name, label: u.name, color: first ? deptColor[first.id] : PALETTE[i % PALETTE.length] }
    })
  }
  if (dim === 'Department') {
    return departments.map((d) => ({ key: d.id, name: d.name, label: d.name, color: deptColor[d.id] }))
  }
  const out: Series[] = []
  for (const d of departments) {
    const cats = categories.filter((c) => c.department === d.id)
    cats.forEach((c, i) =>
      out.push({
        key: c.id,
        name: c.name,
        group: d.name,
        label: c.name === d.name ? c.name : `${d.name} › ${c.name}`,
        color: tint(deptColor[d.id], i),
      }),
    )
    out.push({
      key: UNRESOLVED + d.id,
      name: 'Unresolved',
      group: d.name,
      label: `${d.name} › Unresolved (department level)`,
      color: deptColor[d.id],
      unresolved: true,
    })
  }
  return out
}

/** Consecutive series sharing a legend group (one group, unlabelled, outside the Category view). */
export function groupSeries(series: Series[]): { group?: string; items: Series[] }[] {
  const groups: { group?: string; items: Series[] }[] = []
  for (const s of series) {
    const last = groups[groups.length - 1]
    if (last && last.group === s.group) last.items.push(s)
    else groups.push({ group: s.group, items: [s] })
  }
  return groups
}
